import React, { useState, useEffect, useCallback } from 'react';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash, ListBullets, X } from '@phosphor-icons/react';

interface FieldSchema {
  name: string;
  label: string;
  type: 'number' | 'string' | 'date' | 'boolean';
  required: boolean;
  unit?: string;
}

interface RecordType {
  id?: number;
  recordType: string;
  displayName: string;
  schema: { fields: FieldSchema[] };
  assetCategoryId: number | null;
  isSystemManaged: boolean;
  active: boolean;
}

interface Props {
  categoryId: string;
  isSystemManaged?: boolean;
}

const FIELD_TYPES = [
  { value: 'number', label: 'Getal' },
  { value: 'string', label: 'Tekst' },
  { value: 'date', label: 'Datum' },
  { value: 'boolean', label: 'Ja/Nee' },
];

const ChildRecordTypesSection: React.FC<Props> = ({ categoryId }) => {
  const { session } = useSession();
  const [recordTypes, setRecordTypes] = useState<RecordType[]>([]);
  const fetchRecordTypes = useCallback(async () => {
    if (!session || !categoryId) return;
    try {
      const resp = await fetchWithAuth(
        `${API_URL}/assets/child-record-types?categoryId=${categoryId}`,
        { method: 'GET' },
        session,
      );
      if (resp.ok) setRecordTypes(await resp.json());
    } catch (err) {
      console.error(err);
    }
  }, [session, categoryId]);

  useEffect(() => { fetchRecordTypes(); }, [fetchRecordTypes]);

  const handleAdd = () => {
    setRecordTypes(prev => [...prev, {
      recordType: `custom_${Date.now()}`,
      displayName: '',
      schema: { fields: [{ name: 'value', label: 'Waarde', type: 'number', required: true }] },
      assetCategoryId: parseInt(categoryId),
      isSystemManaged: false,
      active: true,
    }]);
  };

  const handleSave = async (rt: RecordType) => {
    if (!session || !rt.displayName.trim()) {
      toast.error('Naam is verplicht');
      return;
    }

    try {
      // Generate a slug from displayName if new
      const slug = rt.id ? rt.recordType : rt.displayName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

      const payload = {
        ...rt,
        recordType: slug,
        assetCategoryId: parseInt(categoryId),
      };

      let resp: Response;
      if (rt.id) {
        resp = await fetchWithAuth(
          `${API_URL}/assets/child-record-types/${rt.id}`,
          { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) },
          session,
        );
      } else {
        resp = await fetchWithAuth(
          `${API_URL}/assets/child-record-types`,
          { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) },
          session,
        );
      }

      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.message || 'Opslaan mislukt');
      }

      toast.success(`Registratietype "${rt.displayName}" opgeslagen`);
      fetchRecordTypes();
    } catch (err: any) {
      toast.error(err?.message || 'Fout bij het opslaan');
    }
  };

  const handleDelete = async (rt: RecordType, index: number) => {
    if (!session) return;
    if (rt.isSystemManaged) {
      toast.error('Systeembeheerde types kunnen niet verwijderd worden');
      return;
    }

    if (!rt.id) {
      // Not yet saved — just remove from local state
      setRecordTypes(prev => prev.filter((_, i) => i !== index));
      return;
    }

    try {
      const resp = await fetchWithAuth(
        `${API_URL}/assets/child-record-types/${rt.id}`,
        { method: 'DELETE' },
        session,
      );
      if (resp.ok) {
        toast.success('Registratietype verwijderd');
        fetchRecordTypes();
      }
    } catch (err: any) {
      toast.error(err?.message || 'Fout bij het verwijderen');
    }
  };

  const updateField = (rtIndex: number, fieldIndex: number, updates: Partial<FieldSchema>) => {
    setRecordTypes(prev => prev.map((rt, i) => {
      if (i !== rtIndex) return rt;
      const fields = [...rt.schema.fields];
      fields[fieldIndex] = { ...fields[fieldIndex], ...updates };
      return { ...rt, schema: { fields } };
    }));
  };

  const addField = (rtIndex: number) => {
    setRecordTypes(prev => prev.map((rt, i) => {
      if (i !== rtIndex) return rt;
      return {
        ...rt,
        schema: {
          fields: [...rt.schema.fields, { name: '', label: '', type: 'string' as const, required: false }],
        },
      };
    }));
  };

  const removeField = (rtIndex: number, fieldIndex: number) => {
    setRecordTypes(prev => prev.map((rt, i) => {
      if (i !== rtIndex) return rt;
      return {
        ...rt,
        schema: { fields: rt.schema.fields.filter((_, fi) => fi !== fieldIndex) },
      };
    }));
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
            4
          </div>
          <div>
            <h3 className="font-semibold text-base">Registratietypes</h3>
            <p className="text-sm text-muted-foreground">
              Definieer herhalende datastructuren (bijv. meterstanden) die als lijst aan voorzieningen gekoppeld worden
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleAdd}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Registratietype toevoegen
        </Button>
      </div>

      {recordTypes.length === 0 ? (
        <div className="text-center py-8 text-sm text-muted-foreground border border-dashed rounded-lg">
          Geen registratietypes geconfigureerd voor deze categorie
        </div>
      ) : (
        <div className="space-y-4">
          {recordTypes.map((rt, rtIndex) => (
            <div key={rt.id || rtIndex} className="rounded-lg border p-4 space-y-3">
              {/* Header */}
              <div className="flex items-center gap-3">
                <ListBullets className="h-4 w-4 text-muted-foreground" />
                <Input
                  value={rt.displayName}
                  onChange={(e) => setRecordTypes(prev => prev.map((r, i) =>
                    i === rtIndex ? { ...r, displayName: e.target.value } : r
                  ))}
                  placeholder="Naam (bijv. Meterstand, Keuring)"
                  className="h-8 text-sm font-medium max-w-xs"
                  disabled={rt.isSystemManaged}
                />
                {rt.isSystemManaged && (
                  <Badge variant="secondary" className="text-[10px]">Systeembeheerd</Badge>
                )}
                <div className="ml-auto flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => handleSave(rt)}
                  >
                    Opslaan
                  </Button>
                  {!rt.isSystemManaged && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={() => handleDelete(rt, rtIndex)}
                    >
                      <Trash className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Fields */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">Velden</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[11px]"
                    onClick={() => addField(rtIndex)}
                    disabled={rt.isSystemManaged}
                  >
                    <Plus className="h-3 w-3 mr-0.5" />
                    Veld
                  </Button>
                </div>
                {rt.schema.fields.map((field, fieldIndex) => (
                  <div key={fieldIndex} className="flex items-center gap-2 bg-muted/30 rounded p-2">
                    <Input
                      value={field.label}
                      onChange={(e) => {
                        const label = e.target.value;
                        const name = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
                        updateField(rtIndex, fieldIndex, { label, name });
                      }}
                      placeholder="Veldnaam"
                      className="h-7 text-xs flex-1"
                      disabled={rt.isSystemManaged}
                    />
                    <Select
                      value={field.type}
                      onValueChange={(v) => updateField(rtIndex, fieldIndex, { type: v as any })}
                      disabled={rt.isSystemManaged}
                    >
                      <SelectTrigger className="h-7 text-xs w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FIELD_TYPES.map(ft => (
                          <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={field.unit || ''}
                      onChange={(e) => updateField(rtIndex, fieldIndex, { unit: e.target.value || undefined })}
                      placeholder="Eenheid"
                      className="h-7 text-xs w-20"
                      disabled={rt.isSystemManaged}
                    />
                    <label className="flex items-center gap-1 text-[11px] whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateField(rtIndex, fieldIndex, { required: e.target.checked })}
                        className="rounded border-input"
                        disabled={rt.isSystemManaged}
                      />
                      Verplicht
                    </label>
                    {!rt.isSystemManaged && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground hover:text-destructive"
                        onClick={() => removeField(rtIndex, fieldIndex)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default ChildRecordTypesSection;
